/**
 * Solanaland
 *
 * Author: Phia Nasol
 * Company: Digiteer Inc.
 *
 * https://www.digiteer.digital/
 *
 */

import React from "react";
import { Col, Container, Modal, Row } from "react-bootstrap";
import { CSVLink } from "react-csv";
import { Card, Button, Table, Input } from "core/components";
import { ProjectStatus, ProjectTasksFormData } from "core/schema";
import { api } from "core/api";
import { activate_task_admin_project_path, close_task_admin_project_path } from "routes";

type ProjectTasksFormProps = {
  project: ProjectTasksFormData;
};

const ProjectTasksForm = ({ project, projectTasks, setProjectTasks }: ProjectTasksFormProps): JSX.Element => {
  const [showActivateTaskModal, setShowActivateTaskModal] = React.useState(false);
  const [showCloseTaskModal, setShowCloseTaskModal] = React.useState(false);
  const [currentProjectTask, setCurrentProjectTask] = React.useState(null);
  const policy = project.policy;
  const table = React.useRef(null);
  const headers = [
    { label: "Task ID", key: "code" },
    { label: "Task", key: "name" },
    { label: "Weight", key: "weight" },
    { label: "Budget", key: "total_budgeted_amount" },
    { label: "Actual Cost", key: "total_actual_cost" },
    { label: "Variance", key: "cost_variance" },
    { label: "End Date", key: "end_date" },
    { label: "FIC", key: "foreman" },
    { label: "EIC", key: "engineer" },
    { label: "PM", key: "manager" },
    { label: "Status", key: "status" }
  ];
  const data = projectTasks.map((projectTask) => {
    const costVariance = `₱${(+projectTask.cost_variance).toLocaleString("en-PH", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    })}`;

    return {
      code: projectTask.code,
      name: projectTask.name,
      weight: projectTask.weight,
      total_budgeted_amount: projectTask.total_budgeted_amount,
      total_actual_cost: projectTask.total_actual_cost,
      cost_variance: costVariance,
      end_date: projectTask.end_date,
      foreman: $(projectTask.foreman).find("span").text(),
      engineer: $(projectTask.engineer).find("span").text(),
      manager: $(projectTask.manager).find("span").text(),
      status: $(projectTask.status).text()
    };
  });

  const columns = React.useMemo(
    () => [
      {
        id: "id",
        Header: "Id",
        accessor: "id"
      },
      {
        id: 1,
        Header: "Task Name",
        accessor: "name",
        Cell: (props) => {
          return <p className="text-center">{props.value}</p>;
        }
      },
      {
        id: 2,
        Header: "Task",
        accessor: "code",
        Cell: (props) => {
          const taskName = `${props.row.original.name}`;
          const taskCode = `${props.value}`;
          const prerequisiteTask = props.row.original.prerequisite_task;

          return (
            <>
              <div className="text-bold" title={taskCode} data-bs-toggle="tooltip">
                {prerequisiteTask ? `${taskCode} ← ${prerequisiteTask.code}` : `${taskCode}`}
              </div>
              <p style={{ whiteSpace: "pre-wrap", overflowWrap: "break-word" }} title={taskName} data-bs-toggle="tooltip">
                {taskName}
              </p>
            </>
          );
        }
      },
      {
        Header: "Weight",
        accessor: "weight",
        Cell: (props) => {
          return props.value ? <p className="text-center">{props.value}</p> : <p className="text-center">{"-"}</p>;
        }
      },
      {
        Header: "Budget",
        accessor: "total_budgeted_amount",
        Cell: (props) => {
          return (
            <div title={props.value} data-bs-toggle="tooltip">
              <p className="text-truncate text-right">{props.value}</p>
            </div>
          );
        }
      },
      {
        Header: "Actual Cost",
        accessor: "total_actual_cost",
        Cell: (props) => {
          return (
            <div title={props.value} data-bs-toggle="tooltip">
              <p className="text-truncate text-right">{props.value}</p>
            </div>
          );
        }
      },
      {
        Header: "Variance",
        accessor: "cost_variance",
        Cell: (props) => {
          return (
            <div title={props.value} data-bs-toggle="tooltip">
              {props.value < 0 ? (
                <p className="text-truncate text-right" style={{ color: "red" }}>{`(${Math.abs(parseFloat(props.value))?.toLocaleString("en-US", {
                  minimumFractionDigits: 2
                })})`}</p>
              ) : (
                <p className="text-truncate text-right text-bold">
                  {props.value ? parseFloat(props.value).toLocaleString("en-US", { minimumFractionDigits: 2 }) : "-"}
                </p>
              )}
            </div>
          );
        }
      },
      {
        Header: "End Date",
        accessor: "end_date",
        Cell: (props) => {
          return <p className="text-center">{props.value || "-"}</p>;
        }
      },
      {
        Header: "FIC",
        accessor: "foreman",
        Cell: (props) => {
          return <div dangerouslySetInnerHTML={{ __html: props.value }} />;
        }
      },
      {
        Header: "EIC",
        accessor: "engineer",
        Cell: (props) => {
          return <div dangerouslySetInnerHTML={{ __html: props.value }} />;
        }
      },
      {
        Header: "PM",
        accessor: "manager",
        Cell: (props) => {
          return <div dangerouslySetInnerHTML={{ __html: props.value }} />;
        }
      },
      {
        Header: "Status",
        accessor: "status",
        Cell: (props) => {
          return <div className="text-center" dangerouslySetInnerHTML={{ __html: props.value }} />;
        }
      },
      {
        Header: "Actions",
        accessor: "actions",
        Cell: (props) => {
          const prerequisiteTask = props.row.original.prerequisite_task;
          const projectPrerequisiteTask = props.data.find((projectTask) => projectTask.code == prerequisiteTask?.code);
          const isPrerequisiteTaskCompleted = projectPrerequisiteTask ? projectPrerequisiteTask?.status?.includes("Closed") : true;

          return (
            <Row className="text-center">
              {props.row.values.status.includes("Pending") &&
                isPrerequisiteTaskCompleted &&
                project.status == ProjectStatus.OnGoing &&
                policy.activate_task && (
                  <Col>
                    <span
                      className="badge badge--pill badge--success"
                      style={{ borderRadius: 6 + "px" }}
                      title="Activate Task"
                      data-bs-toggle="tooltip"
                    >
                      <a
                        style={{ cursor: "pointer" }}
                        onClick={() => {
                          setCurrentProjectTask(props.row.original);
                          setShowActivateTaskModal(!showActivateTaskModal);
                        }}
                      >
                        <p style={{ color: "white" }}>Activate Task</p>
                      </a>
                    </span>
                  </Col>
                )}
              {props.row.values.status.includes("Active") &&
                [ProjectStatus.OnGoing, ProjectStatus.Completed].includes(project.status) &&
                policy.close_task && (
                  <Col>
                    <span
                      className="badge badge--pill badge--completed"
                      style={{ borderRadius: 6 + "px" }}
                      title="Close Task"
                      data-bs-toggle="tooltip"
                    >
                      <a
                        style={{ cursor: "pointer" }}
                        onClick={() => {
                          setCurrentProjectTask(props.row.original);
                          setShowCloseTaskModal(!showCloseTaskModal);
                        }}
                      >
                        <p style={{ color: "white" }}>Close Task</p>
                      </a>
                    </span>
                  </Col>
                )}
            </Row>
          );
        }
      }
    ],
    []
  );

  const updateProjectTasks = (projectTasks, updatedProjectTask) => {
    const updatedProjectTasks = projectTasks.map((projectTask) => {
      if (projectTask.id == updatedProjectTask.id) {
        return { ...projectTask, ...updatedProjectTask };
      }

      return projectTask;
    });

    setProjectTasks(updatedProjectTasks);
  };

  const activateProjectTask = React.useCallback(async (projectTasks, projectTaskId) => {
    try {
      const response = await api.put(activate_task_admin_project_path({ id: project.id }, { project_task_id: projectTaskId }));
      updateProjectTasks(projectTasks, response.data);
    } catch (error) {
      console.error(error);
    }
  }, []);

  const closeProjectTask = React.useCallback(async (projectTasks, projectTaskId) => {
    try {
      const response = await api.put(close_task_admin_project_path({ id: project.id }, { project_task_id: projectTaskId }));
      updateProjectTasks(projectTasks, response.data);
    } catch (error) {
      console.error(error);
    }
  }, []);

  return (
    <Card.Body>
      <Container fluid={true}>
        <Row>
          <Col>
            <h5 className="mb-4">Project Tasks</h5>
          </Col>
          <Col sm="auto">
            <CSVLink headers={headers} data={data} filename={`Project-Tasks(${project.code}).csv`} className="button button--primary button--xsmall">
              Export CSV
            </CSVLink>
          </Col>
        </Row>
        <Row className="mb-4">
          <Col>
            <Input placeholder="Search for Task ID" onChange={(e) => table.current.setFilter(2, e.target.value)} />
          </Col>
          <Col>
            <Input placeholder="Search for Task Name" onChange={(e) => table.current.setFilter(1, e.target.value)} />
          </Col>
        </Row>
        <Row>
          <Col>
            <Table columns={columns} data={projectTasks} hiddenColumns={[1]} ref={table} />
          </Col>
        </Row>
      </Container>
      <Modal dialogClassName="modal-dialog-centered" show={showActivateTaskModal}>
        <Modal.Header>{`Activate Task: ${currentProjectTask?.code}`}</Modal.Header>
        <Modal.Body>{`Are you sure you want to activate project task ${currentProjectTask?.name}?`}</Modal.Body>
        <Modal.Footer>
          <Button
            onClick={() => {
              setShowActivateTaskModal(!showActivateTaskModal);
            }}
            icon="times"
            title="Cancel"
          />
          <Button
            className="button--brand"
            icon="save"
            title="Yes, activate task."
            onClick={() => {
              activateProjectTask(projectTasks, currentProjectTask.id).then(() => {
                setShowActivateTaskModal(!showActivateTaskModal);
              });
            }}
          />
        </Modal.Footer>
      </Modal>
      <Modal dialogClassName="modal-dialog-centered" show={showCloseTaskModal}>
        <Modal.Header>{`Close Task: ${currentProjectTask?.code}`}</Modal.Header>
        <Modal.Body>{`Are you sure you want to close project task ${currentProjectTask?.name}?`}</Modal.Body>
        <Modal.Footer>
          <Button
            onClick={() => {
              setShowCloseTaskModal(!showCloseTaskModal);
            }}
            icon="times"
            title="Cancel"
          />
          <Button
            className="button--success"
            icon="save"
            title="Yes, close task."
            onClick={() => {
              closeProjectTask(projectTasks, currentProjectTask.id).then(() => {
                setShowCloseTaskModal(!showCloseTaskModal);
              });
            }}
          />
        </Modal.Footer>
      </Modal>
    </Card.Body>
  );
};

export default ProjectTasksForm;
